import { cruiseOfferTileClick, offerClicked, tourv2OfferTileClick } from 'analytics/eventDefinitions'
import { addGTMEvent, fireInteractionEvent } from 'api/googleTagManager'
import CurrencyContext from 'contexts/currencyContext'
import useOffer from 'hooks/Offers/useOffer'
import noop from 'lib/noop'
import { isCruiseOffer, isLEOffer, isTourV2Offer } from 'lib/offerTypes'
import React, { useCallback, useContext, useRef, Suspense } from 'react'
import { InView, useInView } from 'react-intersection-observer'
import FrontPageTile from '../OfferListTiles/FrontPageOfferTiles/FrontPageTile'
import SearchTile from '../OfferListTiles/SearchOfferTiles/SearchTile'
import OfferListEventsContext, { OfferListEvents } from './OfferListEventsContext'
import OfferListLoadingOfferTile from './OfferListLoadingOfferTile'
import { EventDataKey } from 'home/pages/HomePage/useHomepageAnalytics'
import HeroOfferTile from '../OfferListTiles/HeroOfferTiles/HeroOfferTile'

interface Props {
  tileStyle: 'search' | 'frontpage' | 'hero';
  offerId: string;
  filters?: App.OfferListFilters;
  position: number;
  eagerLoadImage?: boolean;
  className?: string;
  fetchOnlyIfInView?: boolean;
  onInViewChange?: (inView: boolean) => void;
  /**
   * The % of the tile that needs to be in view before being considered 'in view'
   * Value between 0 and 1 (e.g. 0.1 = 10% of it)
   * */
  inViewThreshhold?: number;
}

const tileStyleMap = {
  search: SearchTile,
  frontpage: FrontPageTile,
  hero: HeroOfferTile,
}

/**
 * Core offer list tile that will render the appropriate tile based on the type of item given
 */
function OfferListOfferTile(props: Props) {
  const {
    tileStyle,
    offerId,
    position,
    filters,
    eagerLoadImage,
    className,
    inViewThreshhold = 0.5,
    onInViewChange = noop,
    fetchOnlyIfInView,
  } = props

  const currency = useContext(CurrencyContext)

  const { ref: rootRef, inView: rootInView } = useInView({
    triggerOnce: true,
    initialInView: !fetchOnlyIfInView,
  })

  const Tile = tileStyleMap[tileStyle]
  const fireOfferListEvent = useContext(OfferListEventsContext) ?? noop
  const [offer, , error] = useOffer(offerId, { requireSummaryOnly: true, disabled: !rootInView })
  const inViewFired = useRef<boolean>(false)

  const onProductClick = useCallback(() => {
    if (isLEOffer(offer)) {
      // TODO: remove this once G4A is up?
      // we only know how to register le offer clicks at the moment
      addGTMEvent(offerClicked(offer, currency, position))
    }

    if (isCruiseOffer(offer)) {
      fireInteractionEvent(cruiseOfferTileClick(offer.id))
    } else if (isTourV2Offer(offer)) {
      fireInteractionEvent(tourv2OfferTileClick(offer.id))
    }

    if (tileStyle === 'hero') {
      fireOfferListEvent(OfferListEvents.productClick, { offer, position, key: EventDataKey.ProductClick })
    } else {
      fireOfferListEvent(OfferListEvents.productClick, { offer, index: position })
    }
  }, [offer, tileStyle, currency, position, fireOfferListEvent])

  const inViewChanged = useCallback((inView: boolean) => {
    if (inView && !inViewFired.current) {
      // we only want to trigger in view event once but
      // we do want to keep notifying listeners it's changing
      inViewFired.current = true

      if (tileStyle === 'hero') {
        fireOfferListEvent(OfferListEvents.impression, {
          offer, position, key: EventDataKey.ProductImpression,
        })
      } else {
        fireOfferListEvent(OfferListEvents.impression, {
          offer, index: position,
        })
      }
    }
    onInViewChange(inView)
  }, [fireOfferListEvent, offer, onInViewChange, position, tileStyle])

  if (error) {
    return null
  }
  return (
    <div ref={rootRef}>
      {!offer && (
        <OfferListLoadingOfferTile className={className} offerId={offerId} tileStyle={tileStyle} />
      )}
      {offer && (
        <Suspense fallback={<OfferListLoadingOfferTile offerId={offerId} tileStyle={tileStyle} />}>
          <InView threshold={inViewThreshhold} rootMargin="0px" onChange={inViewChanged}>
            <Tile
            className={className}
            offer={offer as App.Offer}
            eagerLoadFirstImage={eagerLoadImage}
            filters={filters}
            productClick={onProductClick}
          />
          </InView>
        </Suspense>
      )}
    </div>
  )
}

OfferListOfferTile.defaultProps = {
  columns: 1,
}

export default React.memo(OfferListOfferTile)
